import { Button, Group, Modal, PasswordInput, Select, Stack, TextInput } from '@mantine/core';
import { DateInput } from '@mantine/dates';
import { useForm } from '@mantine/form';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { notifications } from '@mantine/notifications';
import { useTranslation } from 'react-i18next';
import { SpecialtyInput } from './SpecialtyInput';
import api from '../services/api';
import { GenderInput } from './QuickCreate';
import { MultiPhoneEditor, MultiAddressEditor, PhoneItem, AddressItem } from './MultiContact';
import { uzDateProps } from '../lib/dateInput';

/**
 * Xodim/o'qituvchi YARATISH modali — ikki sahifada bir xil.
 *
 * Ilgari faqat "Xodimlar" sahifasida edi, "O'qituvchilar" sahifasida esa qo'shish
 * tugmasi UMUMAN yo'q edi (egasi, 10.09: «O'qituvchi qo'shish tugmasi yo'q») —
 * o'qituvchi qo'shish uchun boshqa sahifaga o'tish kerak bo'lardi. Nusxa ko'chirish
 * o'rniga modal shu yerga chiqarildi: forma qoidalari (login uzunligi, jins
 * majburiyligi) ikki joyda ajralib ketmasin.
 *
 * `roles` — tanlash mumkin bo'lgan rollar. O'qituvchilar sahifasidan faqat
 * o'qituvchi rollari beriladi: u sahifadan tasodifan direktor yaratilib qolmasin.
 */
export function StaffCreateModal({
  opened, onClose, roles, title,
}: {
  opened: boolean;
  onClose: () => void;
  roles: string[];
  title: string;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();

  const form = useForm({
    initialValues: {
      fullName: '', login: '', password: '', role: roles[0] ?? 'TEACHER',
      gender: '', phone: '', birthDate: null as Date | null, specialty: '',
      phones: [] as PhoneItem[], addresses: [] as AddressItem[],
    },
    validate: {
      fullName: (v) => (!v ? t('staffPage.fullNameRequired') : null),
      login: (v) => (v.length < 3 ? t('staffPage.loginMin') : null),
      password: (v) => (v.length < 5 ? t('staffPage.passwordMin') : null),
      gender: (v) => (!v ? t('staffPage.genderRequired') : null),
    },
  });

  const createMut = useMutation({
    mutationFn: (v: typeof form.values) => api.post('/staff', v),
    onSuccess: () => {
      notifications.show({ color: 'green', message: t('staffPage.created') });
      /* Yaratilgan xodim IKKALA ro'yxatga ham tushishi mumkin (rolga qarab),
         shuning uchun har ikkalasi va bog'liq tanlagichlar yangilanadi. */
      qc.invalidateQueries({ queryKey: ['staff-all'] });
      qc.invalidateQueries({ queryKey: ['staff-specialties'] }); // yangi qiymat takliflarga qo'shilsin
      qc.invalidateQueries({ queryKey: ['teachers-page'] });
      qc.invalidateQueries({ queryKey: ['teachers'] });
      form.reset();
      onClose();
    },
  });

  const roleOptions = roles.map((r) => ({ value: r, label: t(`roles.${r}`) }));

  return (
    <Modal opened={opened} onClose={onClose} title={title} centered>
      <form onSubmit={form.onSubmit((v) => createMut.mutate(v))}>
        <Stack>
          <TextInput label={t('staffPage.fullName')} required {...form.getInputProps('fullName')} />
          <SpecialtyInput
            value={form.values.specialty}
            onChange={(v) => form.setFieldValue('specialty', v)}
          />
          <GenderInput {...form.getInputProps('gender')} />
          {/* Bitta rol qolsa tanlash ma'nosiz — maydon ko'rsatilmaydi. */}
          {roleOptions.length > 1 && (
            <Select label={t('staffPage.role')} data={roleOptions} required {...form.getInputProps('role')} />
          )}
          {/* `uzDateProps` — qo'lda terish ham ishlaydi (`lib/dateInput.ts`). */}
          <DateInput label={t('staffPage.birthDateOptional')} {...uzDateProps} {...form.getInputProps('birthDate')} />
          <TextInput label={t('staffPage.login')} required {...form.getInputProps('login')} />
          <PasswordInput label={t('staffPage.password')} required {...form.getInputProps('password')} />
          <MultiPhoneEditor value={form.values.phones} onChange={(v) => form.setFieldValue('phones', v)} />
          <MultiAddressEditor value={form.values.addresses} onChange={(v) => form.setFieldValue('addresses', v)} />
          <Group justify="flex-end" mt="sm">
            <Button variant="default" onClick={onClose}>{t('common.cancel')}</Button>
            <Button type="submit" loading={createMut.isPending}>{t('staffPage.create')}</Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}
